import React, { useMemo } from 'react';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import type { ChatMessage } from '../types/messages';
import type { UserInfo } from '../types/user';
import type { ProposalNotification } from '../hooks/useAiMessages';
import type { ProposalBundle } from '../api/proposalBundles';
import type { ReservationInfo } from '../api/reservationInfo';
import type { FormValues } from './message_section/ReservationInfoFormTypes';
import SidebarMenu from './SidebarMenu';
import ChatArea from './ChatArea';
import InfoPanel from './InfoPanel';
import RulesPanel from './RulesPanel';
import SessionWarningModal from './SessionWarningModal';

interface Props {
  tripId: string;
  trip: TripResponseArgs | null;
  user?: UserInfo;
  pusherKey: string;
  pusherChannelPrefix: string;
  headerTitle?: string;
  subtitle?: string;
  children?: React.ReactNode;
  enableMessaging?: boolean;
  initialMessages?: ChatMessage[];
  onProposalReceived?: (notification: ProposalNotification) => void;
  onReservationFormReceived: () => void;
  proposalBundles?: ProposalBundle[];
  onSelectItem?: (bundleId: number, itemId: number) => void;
  onCloseItem?: (bundleId: number) => void;
  reservationInfo?: ReservationInfo | null;
  onSubmitReservationInfo?: (values: FormValues) => Promise<boolean>;
  isSubmittingReservationInfo?: boolean;
  onArrangementRequestSuccess?: () => Promise<void>;
}

const TripAiMessagesContainer = ({
  tripId,
  trip,
  user,
  pusherKey,
  pusherChannelPrefix,
  headerTitle,
  subtitle,
  children,
  enableMessaging,
  initialMessages = [],
  onProposalReceived,
  onReservationFormReceived,
  proposalBundles = [],
  onSelectItem,
  onCloseItem,
  reservationInfo,
  onSubmitReservationInfo,
  isSubmittingReservationInfo,
  onArrangementRequestSuccess
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const isTripLoading = trip === null;

  return (
    <div style={styles.root}>
      <div style={styles.body}>
        <SidebarMenu currentTripId={tripId} />
        <ChatArea
          tripId={tripId}
          trip={trip}
          user={user}
          pusherKey={pusherKey}
          pusherChannelPrefix={pusherChannelPrefix}
          headerTitle={headerTitle}
          subtitle={subtitle}
          enableMessaging={!isTripLoading && enableMessaging !== false}
          initialMessages={initialMessages}
          onProposalReceived={onProposalReceived}
          onReservationFormReceived={onReservationFormReceived}
          proposalBundles={proposalBundles}
          onSelectItem={onSelectItem}
          onCloseItem={onCloseItem}
          reservationInfo={reservationInfo}
          onSubmitReservationInfo={onSubmitReservationInfo}
          isSubmittingReservationInfo={isSubmittingReservationInfo}
          onArrangementRequestSuccess={onArrangementRequestSuccess}
        >
          {children}
        </ChatArea>
        {trip ? <InfoPanel trip={trip} proposalBundles={proposalBundles} /> : <PlaceholderPanel />}
        <RulesPanel />
        <SessionWarningModal />
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  root: {
    width: '100%',
    height: 'calc(100vh - 80px)',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    minHeight: 0
  },
  body: {
    display: 'flex' as const,
    flex: 1,
    position: 'relative' as const,
    backgroundColor: theme.whiteColor,
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    overflow: 'hidden',
    minHeight: 0
  }
});

const PlaceholderPanel = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(
    () => ({
      width: '320px',
      padding: '20px',
      backgroundColor: theme.contentBgColor,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }),
    [theme]
  );
  return (
    <div style={styles}>
      <Loading />
    </div>
  );
};

export default TripAiMessagesContainer;
